/* eslint-disable indent */
import {
  getUserNameUsingGET as getUserName,
  getProcessByIdUsingGET as getProcess,
  getProcessesUsingGET as getProcesses,
  createProcessUsingPOST as createProcess,
  getRolesUsingGET as getRoles,
  getValidationRulesUsingGET as getValidationRule
} from '@/utils/api'
import { format } from 'date-fns'
import { isProxy, toRaw } from 'vue';
import store from '@/store';
import { Marked } from 'marked';
import DOMPurify from 'dompurify';

// Pin marked v12 behavior at module load (defends against future default-drift).
const markedInstance = new Marked({ gfm: true, breaks: false, async: false });

export const arraysEqual = (arr1, arr2) => {
  if (arr1 == null || arr2 == null) {
    return arr1 === arr2
  }

  if (arr1.length !== arr2.length) {
    return false
  }

  return arr1.every((value, index) => value === arr2[index])
}

export const filterEmptyExpressions = (arr) => arr.filter(item => item && item.trim() !== '')

export const objectsEqual = (obj1, obj2) => {
  if ((!obj1 || !obj2) || (obj1 && !obj2) || (!obj1 && obj2)) {
    return false
  }
  if (Object.keys(obj1).length !== Object.keys(obj2).length) {
    return false
  }

  return Object.keys(obj1).every((key) => obj1[key] === obj2[key])
}

export const safeJSONParse = (str) => {
  try {
    return JSON.parse(str)
  } catch (e) {
    return null
  }
}

export const predictBestTimeUnitFromMsValue = async (value) => {
  if (Number(value / 1000 / 60 / 60 / 24).toFixed() === String(value / 1000 / 60 / 60 / 24)) return { value: (value / 1000 / 60 / 60 / 24), unit: 'Day' }
  if (Number(value / 1000 / 60 / 60).toFixed() === String(value / 1000 / 60 / 60)) return { value: (value / 1000 / 60 / 60), unit: 'Hr' }
  if (Number(value / 1000 / 60).toFixed() === String(value / 1000 / 60)) return { value: (value / 1000 / 60), unit: 'Min' }
  if (Number(value / 1000).toFixed() === String(value / 1000)) return { value: (value / 1000), unit: 'Sec' }

  return { value: value, unit: 'MSec' }
}

export const predictBestSizeUnitFromBytesValue = async (value) => {
  if (!value) return { value: 0, unit: 'Bytes' }

  const gb = value / (1024 ** 3)
  const mb = value / (1024 ** 2)
  const kb = value / 1024

  if (gb >= 1) return { value: parseFloat(gb.toFixed(2)), unit: 'Gigabytes' }
  if (mb >= 1) return { value: parseFloat(mb.toFixed(2)), unit: 'Megabytes' }
  if (kb >= 1) return { value: parseFloat(kb.toFixed(2)), unit: 'Kilobytes' }

  return { value: value, unit: 'Bytes' }
}

export const fetchUserData = async (id) => {
  const data = await getUserName({ id })

  return { id, ...data.data.data }
}

export const getResourceForRestore = () => {
  const resourceToRestoreAfterDelete = store.state.app.resourceToRestoreAfterDelete;
  store.dispatch('app/clearResourceFromRestoreAfterDelete');
  return resourceToRestoreAfterDelete || null;
}

const msToHMS = (ms) => {
  if (ms < 1001) return ms + ' ms'

  if (ms < 60000) return (ms / 1000).toFixed(2) + ' sec'

  if (ms < 3600000) return (ms / 1000 / 60).toFixed(2) + ' min'

  if (ms < 86400000) { // Less than a day
    const date = new Date(ms)
    const hours = date.getUTCHours()
    const minutes = date.getUTCMinutes()

    return (hours < 10 ? '0' : '') + hours + ':' + (minutes < 10 ? '0' : '') + minutes + ' HH:mm'
  } else { // More than a day
    const days = Math.floor(ms / 86400000)
    const hours = Math.floor((ms % 86400000) / 3600000)
    const minutes = Math.floor((ms % 3600000) / 60000)

    return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours > 1 ? 's' : ''}, and ${minutes} minute${minutes > 1 ? 's' : ''}`
  }
}

export const calculateDuration = (startTime, endTime) => {

  if (!startTime || !endTime) return msToHMS(0)

  return msToHMS(new Date(endTime).valueOf() - new Date(startTime).valueOf())
}

export const roleChecker = (userRoles, rolesArray) => {
  return !(userRoles && userRoles.some((role) => rolesArray.includes(role.name)))
}

export const recursion = (arr, localStep) => {
  let stepValue = []
  let bool = false

  arr.forEach((item) => {
    const id = typeof localStep?.localId === 'string' ? localStep?.localId.split('.')[0] : localStep?.localId
    const itemId = typeof item?.localId === 'string' ? item?.localId.split('.')[0] : item?.localId

    if (itemId > id) {
      bool = true
    } else {
      bool = false
      if (localStep?.localId + '' >= item?.localId + '' && ('' + item.localId)?.split('.')[0] === ('' + localStep?.localId)?.split('.')[0]) {
        if (item.type === 'FOREACH') {
          stepValue.push({ 'key': item?.properties?.recordName })
        }
      }
    }

    if (item.properties?.set && !bool && (localStep?.localId + '' >= item?.localId + '' || +localStep?.localId > +item?.localId)) {
      for (const key in item?.properties?.set) {
        stepValue.push({
          key,
          localId: item.localId,
          value: item.properties.set[key],
          type: item.type
        })
      }
    } else if (item.type === 'FOREACH' && !bool) {
      if (item.properties?.steps.length) {
        const stepsArray = recursion(item.properties?.steps, stepValue)

        stepsArray.forEach((step) => {
          stepValue.push(step)
        })
      }
    } else if (item.type === 'UNSET_VARIABLES' && !bool) {
      stepValue = stepValue.filter((el) => {
        return !item?.properties?.variables.find((objFromB) => {
          return el.key === objFromB
        })
      })

    } else if (isJdbcTransaction(item) && item?.properties?.parameters?.steps?.length && !bool) {
      const stepsArray = recursion(item.properties.parameters.steps, stepValue)

      stepsArray.forEach((step) => {
        stepValue.push(step)
      })
    } else if ((item?.properties?.steps?.length > 0 || item?.steps?.length) && !bool) {
      let stepsArray = []

      if (item?.steps?.length) {
        stepsArray = recursion(item?.steps, stepValue)
      } else {
        stepsArray = recursion(item?.properties?.steps, stepValue)

      }

      stepsArray.forEach((step) => {
        stepValue.push(step)
      })
    } else if ((item?.properties?.conditions?.length > 0 || item?.properties?.batches?.length > 0) && !bool) {
      const stepsArray = recursion(item?.properties?.conditions || item?.properties?.batches, stepValue)

      stepsArray.forEach((step) => {
        stepValue.push(step)
      })
    }

    if ((item.type === 'MONGODB' ||
      item.type === 'CSV' ||
      item.type === 'EXECUTE_EXTERNAL_COMMAND' ||
      item.type === 'IMAGE' ||
      item.type === 'JWT' ||
      item.type === 'PDF' ||
      item.type === 'UUID') &&
      !bool) {
      if (item.properties?.targetObject)
        stepValue.push({ 'key': item.properties?.targetObject })
    } else if (item.type === 'JDBC' && !bool) {
      if (item.properties?.parameters?.targetObject)
        stepValue.push({ 'key': item.properties?.parameters?.targetObject })
    } else if (item.type === 'USER') {
      if (item.properties?.fields?.targetObject)
        stepValue.push({ 'key': item.properties?.fields?.targetObject })
    } else if (item.type === 'REST' && !bool) {
      if (item.properties?.restResponseCode)
        stepValue.push({ 'key': item.properties?.restResponseCode })
      if (item.properties?.targetObject)
        stepValue.push({ 'key': item.properties?.targetObject })
    } else if (item.type === 'EXECUTE_PROCESS') {
      const inputKeys = item.properties?.input !== null ? Object.keys(item.properties?.input) : []
      const outputKeys = item.properties?.output !== null ? Object.keys(item.properties?.output) : []
      const inputOutputArr = [...inputKeys, ...outputKeys]

      inputOutputArr.forEach((key) => {
        stepValue.push({ 'key': key })
      })
    }
  })

  return stepValue
}

export const isJdbcTransaction = (step) =>
  step?.type === 'JDBC' && step?.properties?.action === 'TRANSACTION'

export const getAllStepsData = (processSteps, formatFunction = (step) => step) => {
  const allSteps = []

  const addStepWithInnerSteps = (stepData, isCatch = false) => {
    const step = isCatch
      ? { steps: [...stepData], name: 'catch', localId: 'catch' }
      : { ...stepData }

    allSteps.push(formatFunction(step))

    if (step.properties?.conditions?.length > 0) {
      step.properties?.conditions.forEach((condition) => {
        addStepWithInnerSteps(condition)
      })
    }

    if (step.properties?.batches?.length > 0) {
      step.properties?.batches.forEach((condition) => {
        addStepWithInnerSteps(condition)
      })
    }

    const innerSteps = step.properties?.steps || step.steps || []

    if (innerSteps.length > 0) {
      innerSteps.forEach((subStep) => {
        addStepWithInnerSteps(subStep)
      })
    }

    if (isJdbcTransaction(step) && step.properties?.parameters?.steps?.length > 0) {
      step.properties.parameters.steps.forEach((subStep) => {
        addStepWithInnerSteps(subStep)
      })
    }

    if (step.type === 'TRY_CATCH') {
      if (step?.properties?.try) addStepWithInnerSteps(step.properties.try, false)
      if (step?.properties?.catch) addStepWithInnerSteps(step.properties.catch, true)
      if (step?.properties?.finally) addStepWithInnerSteps(step.properties.finally, false)
    }
  }

  processSteps.forEach((step) => {
    addStepWithInnerSteps(step)
  })

  return allSteps
}

export const addDoubleQuotesIfString = (value) => {
  let formattedValue = ''

  switch (typeof value) {
    case 'string':
      if (value.startsWith('"') && value.endsWith('"')) {
        formattedValue = value

      } else if (isJavaVariable(value)) {
        formattedValue = value

      } else if (value.startsWith('"') || value.endsWith('"')) {
        formattedValue = `"${value.replace(/\\?"/g, '\\"')}"`

      } else {
        formattedValue = `"${value}"`
      }
      break
    case 'object':
      if (value !== null) {
        formattedValue = value
      } else {
        formattedValue = 'null'
      }
      break
    case 'NaN':
      formattedValue = 'NaN'
      break
    case 'undefined':
      formattedValue = 'undefined'
      break
    default:
      formattedValue = value
  }

  return formattedValue
}

export const removeAdditionalPropertiesFromSteps = (
  allSteps,
  onlyDeleteSelectionIndicatorsAndSimpleCleaning = false
) => {
  const localAllSteps = JSON.parse(JSON.stringify(allSteps))

  const searchStep = (items) => {
    items.forEach((item) => {

      if (item.subType === 'QUERY' || item.subType === 'BATCH') {
        delete item.enabled
        delete item.properties
      }

      delete item.isSelected
      delete item.logStatus
      if (item.properties?.catch?.length) {

        searchStep(item.properties.catch)
        item.properties.catch.forEach((item2) => {
          delete item2.isSelected
          delete item2.id
          searchStep(item2.steps)
        })
      }
      if (item.properties?.try) {
        delete item.properties?.try?.isSelected
        delete item.properties?.try?.subType
        if (item.properties.try.steps.length > 0) {
          searchStep(item.properties.try.steps)
          item.properties.try.steps.forEach((item2) => {
            delete item2.isSelected
          })
        }
      }
      if (item.properties?.finally) {
        delete item.properties?.finally?.isSelected
        if (item.properties.finally.steps.length > 0) {
          searchStep(item.properties.finally.steps)
          item.properties.finally.steps.forEach((item2) => {
            delete item2.isSelected
          })
        }
      }
      if (item.properties?.conditions?.length) {
        searchStep(item.properties.conditions)
        item.properties.conditions.forEach((item2) => {
          delete item2.isSelected
          delete item2.id
          searchStep(item2.steps)
        })
      }
      if (item.properties?.batches?.length) {
        searchStep(item.properties.batches)
        item.properties.batches.forEach((item2) => {
          delete item2.isSelected
          delete item2.id
          searchStep(item2.steps)
        })
      }
      if ((item.type === 'JS' || item.type === 'GROOVY' || item.type === 'PYTHON') &&
        (!item.librariesNames
          || (Array.isArray(item.librariesNames)
            && !item.librariesNames?.length))
      ) item.librariesNames = null

      // We need to remove double quotes to input and output values
      // to avoid unexpected "Unsaved changed" modal
      if (item.type === 'EXECUTE_PROCESS') {
        //TODO DRY fors
        for (const key in item.properties.input) {
          const value = item.properties.input[key]

          const formattedValue = removeEnclosingQuotes(value)

          item.properties.input[key] = formattedValue
        }
        for (const key in item.properties.output) {
          const value = item.properties.output[key]

          const formattedValue = removeEnclosingQuotes(value)

          item.properties.output[key] = formattedValue
        }
      }

      for (const row in item.properties) {
        // eslint-disable-next-line no-prototype-builtins
        if (item.properties.hasOwnProperty(row)) {
          if (row !== 'steps' && !item.properties[row]) item[row] = item.properties[row] = null
          if (row === 'librariesNames') {
            if (
              !item.properties?.librariesNames
              || (Array.isArray(item.properties?.librariesNames)
                && !item.properties?.librariesNames?.length)
            ) item.properties.librariesNames = null
          }
          if (row === 'attachments') {
            if (!item.properties?.attachments || (Array.isArray(item.properties?.attachments) && !item.properties?.attachments?.length)) item.properties.attachments = null
          }
        }
      }
      if (
        item.properties &&
        item.properties.steps &&
        item.properties.steps.length > 0
      ) {
        searchStep(item.properties.steps)
      }
      if (
        isJdbcTransaction(item) &&
        item.properties?.parameters?.steps &&
        item.properties.parameters.steps.length > 0
      ) {
        searchStep(item.properties.parameters.steps)
      }

      delete item.authType

      delete item.attachments

      if (onlyDeleteSelectionIndicatorsAndSimpleCleaning) {
        return
      }

      item.localId = item.localId ? item.localId.toString() : null
      delete item.librariesNames

      if (item.properties?.pluginObj) delete item.properties.pluginObj

      if (item.properties?.librariesNames && item.properties?.librariesNames.length === 0) item.properties.librariesNames = null

      if (item.properties?.attachments && item.properties?.attachments.length === 0) item.properties.attachments = null

      if (item.type === 'REST' && item.properties?.authType === 'NONE') item.properties.authType = null

      delete item.id
    })
  }

  searchStep(localAllSteps)

  return localAllSteps
}

export const doesProcessNameExist = async (processName) => (
  await getProcesses({ name: processName })
).data.data.items.find((x) => x.name === processName)

export const getProcessById = async (id) => {
  return new Promise((resolve, reject) => {
    getProcess({ id })
      .then((res) => {
        resolve(res.data.data)
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export const getProcessByName = async (name) => {
  return new Promise((resolve, reject) => {
    getProcesses({ request: { page: 1, size: 1, filter: { name } } })
      .then((res) => {
        const findCorrectProcess = res.data.data.items.find((x) => x.name === name)

        if (findCorrectProcess) {
          resolve(findCorrectProcess)
        } else {
          resolve(false)
        }
      })
      .catch((err) => {
        reject(err)
      })
  })
}

export const createProcessForTriggerData = async (triggerName, processNameExists = false, roles) => {
  const rolesWithoutAuth = await getRolesWithoutAuth()
  const rolesWithoutAuthIds = rolesWithoutAuth.map((role) => role.id)
  const rolesCleaned = roles.filter((role) =>
    rolesWithoutAuthIds.includes(role.roleId)
  )

  const res = await createProcess({
    body: {
      id: 0,
      createdOn: '',
      modifiedOn: '',
      name: processNameExists
        ? `${triggerName} - ${format(new Date(), 'yyyy-MM-dd HH:mm')}`
        : triggerName,
      status: 'ACTIVE',
      logsTtlInMSec: 86400000,
      errorsTtlInMSec: 86400000,
      maxProcessingDurationInMSec: 60000,
      maxSimultaneousExecutions: 1,
      overallSimultaneousExecutions: 1,
      simultaneousExecutionsPerInstance: 1,
      steps: {
        steps: [
          {
            enabled: true,
            id: 1,
            isSelected: false,
            name: 'Placeholder',
            properties: {
              message: 'Placeholder'
            },
            type: 'LOG',
            enableStepLog: true,
            enableCacheLog: true,
            localId: 1
          }
        ]
      },
      isGdprRelevant: false,
      roles: rolesCleaned,
      isCacheStepLogsEnabled: true,
      isStepLogsEnabled: true,
      comment: '',
      inputValidationRuleName: null,
      outputValidationRuleName: null,
      isSystem: false
    }
  })

  return res
}

export const canUserEditResource = (isSuperUser, editRole, isSystem) => {
  if (isSystem) return false

  if (isSuperUser) return true

  return editRole
}

export const filterUniqueObjectsByDataIdAndName = (value, index, self) => {
  return (
    self.findIndex(
      (obj) => obj.data.name === value.data.name && obj.data.id === value.data.id
    ) === index
  )
}

export const getRolesWithoutAuth = async () => {
  const roles = await getRoles()

  return roles.data.data.items.filter(
    (role) => role.name !== 'AUTHENTICATED' && role.name !== 'UNAUTHENTICATED'
  )
}

export const estimatedTimeOfExecution = (start, end) => {
  if (start && end) {
    const startInner = new Date(start)
    const endInner = new Date(end)
    const diff = endInner - startInner

    return msToHMS(diff)
  }

  return 'N/A'
}

export const checkSftpValues = (username, password, privateKey, passphrase) => {
  // Only username + password
  if (username && password && !privateKey && !passphrase) return true

  // Only username + private key
  if (username && privateKey && !password && !passphrase) return true

  // Only username + private key + passphrase
  if (username && privateKey && passphrase && !password) return true

  // Any other combination is invalid
  return false
}

export const isJavaVariable = (item) => {
  if (!item) return false
  if (typeof item !== 'string') return false

  return item.substring(0, 1) === '$' || item.substring(0, 2) === '$.'
}

export const formatDateTimeToISO = (date) => {
  if (!date) return ''

  // Format to 'YYYY-MM-DDTHH:mm:ss.sssZ'
  const isoString = new Date(date).toISOString()

  // Get the timezone offset in +00:00 format
  const timezoneOffset = -new Date().getTimezoneOffset()
  const sign = timezoneOffset >= 0 ? '+' : '-'
  const hoursOffset = Math.floor(Math.abs(timezoneOffset) / 60)
    .toString()
    .padStart(2, '0')
  const minutesOffset = (Math.abs(timezoneOffset) % 60)
    .toString()
    .padStart(2, '0')
  const offset = `${sign}${hoursOffset}:${minutesOffset}`

  // Combine date and time with offset and wrap in single quotes
  return `'${isoString.slice(0, -1)}${offset}'`
}

export const convertISOToDate = (isoString) => {
  if (!isoString) return ''
  // Remove the leading and trailing single quotes if present
  const cleanString = isoString.replace(/^'(.*)'$/, '$1')

  // Convert the cleaned ISO string to a Date object
  const date = new Date(cleanString)

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date format')
  }

  return date
}

export function parseUtcStringWithoutTZ(str) {
      const [datePart, timePart] = str.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);

      return new Date(Date.UTC(year, month - 1, day, hour, minute));
}

export function setNestedProperty(obj, path, value) {
  const { nestedObj, lastKey } = getNestedPropertyForSetting(obj, path)

  if (nestedObj && lastKey !== undefined) {
    nestedObj[lastKey] = value
  }
}

export const getNestedValue = (obj, key) => {
  let nestedValue = obj[key]

  if (key.includes('.')) {
    const keys = key.split('.')

    keys.forEach((k) => {
      if (nestedValue) {
        nestedValue = nestedValue[k]
      }
    })
  }

  return nestedValue
}

export const getNestedPropertyForSetting = (obj, path) => {
  const keys = path
    .replace(/\[(\d+)]/g, '.$1')
    .split('.')
    .filter(Boolean)

  if (keys.length === 0) {
    return { nestedObj: obj, lastKey: undefined }
  }

  const lastKeyRaw = keys.pop()
  const nestedObj = keys.reduce((acc, key, index) => {
    const normalizedKey = Array.isArray(acc) && /^\d+$/.test(key) ? Number(key) : key

    if (acc[normalizedKey] === undefined || acc[normalizedKey] === null || typeof acc[normalizedKey] !== 'object') {
      const nextKey = keys[index + 1]
      acc[normalizedKey] = /^\d+$/.test(nextKey) ? [] : {}
    }

    return acc[normalizedKey]
  }, obj)

  const lastKey = Array.isArray(nestedObj) && /^\d+$/.test(lastKeyRaw) ? Number(lastKeyRaw) : lastKeyRaw

  return { nestedObj, lastKey }
}

export const deepMerge = (target, source) => {
  const output = { ...target }

  if (target && source) {

    Object.keys(source).forEach((key) => {
      const sourceVal = source[key]
      const targetVal = target[key]

      if (typeof sourceVal === 'object' && sourceVal && !Array.isArray(sourceVal)) {
        output[key] = targetVal && typeof targetVal === 'object'
          ? deepMerge(targetVal, sourceVal)
          : deepMerge({}, sourceVal)
      } else {
        output[key] = sourceVal
      }
    })
  }

  return output
}

export const checkEmail = (v) => {
  const isSplit = v.split(',')

  if (isSplit.length > 1) {
    let correct = true

    isSplit.forEach((testEmail) => {
      if (correct) {
        correct = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(testEmail.trim().toLowerCase())
      }
    })

    if (correct) return true

    return false
  } else {
    return (v && /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v.toLowerCase()))
  }
}

export const filterNumber = (evt) => {
  evt = (evt) ? evt : window.event
  const expect = evt.target.value.toString() + evt.key.toString()

  if (!/^[-+]?[0-9]+$/.test(expect)) {
    evt.preventDefault()

    return false
  } else {
    return true
  }
}

// Helper function to remove enclosing quotes from a value, but don't cast it further
const removeEnclosingQuotes = (value) => {
  if (!value || typeof value !== 'string') return value

  let trimmedValue = value.trim()

  // Remove single or double quotes from start and end
  while (
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith('\'') && trimmedValue.endsWith('\''))
  ) {
    trimmedValue = trimmedValue.slice(1, -1)
  }

  if (trimmedValue === 'null') {
    return null
  } else if (trimmedValue === 'undefined') {
    return undefined
  } else if (trimmedValue === 'NaN') {
    return NaN
  }

  return trimmedValue
}

// Helper function to cast values based on their content
export const castValue = (value) => {
  if (typeof value === 'string') {
    // First, check if the value is enclosed in quotes, if so, remove quotes but don't cast
    const unquotedValue = removeEnclosingQuotes(value)

    // If the value was enclosed in quotes, return the unquoted value without further casting
    if (unquotedValue !== value) {
      return unquotedValue // This ensures we return 'true' as a string, not a boolean
    }

    // Check for numbers
    if (!isNaN(value) && value.trim() !== '') {
      return parseFloat(value)
    }

    // Check for booleans
    if (value.toLowerCase() === 'true') return true
    if (value.toLowerCase() === 'false') return false

    // Check for empty strings
    if (value.trim() === '') return null
  }

  // Return value for arrays and objects if stringified
  try {
    const parsed = JSON.parse(value)

    if (typeof parsed === 'object') {
      return parsed
    }
  } catch (e) {
    // It's not a stringified object/array, leave as is
  }

  // Return the original value if no other condition matches
  return value
}
export const castDefaultValueFromDataType = (value, type) => {
  const normalizedType = type.toLowerCase()

  switch (normalizedType) {
    case 'string':
    case 'varchar':
    case 'timestamp':
    case 'uuid':
      return value === '' || value === null || value === undefined ? null : String(value)

    case 'number':
    case 'bigint':
    case 'integer':
    case 'double':
    case 'float':
      // Check if value is NaN to handle non-numeric strings

      return !isNaN(value) && value && String(value).trim() !== '' ? parseFloat(value)
        : String(value).trim() === '' ? null : value

    case 'boolean':
      // Convert value to boolean based on string contents
      return value === null ? null : String(value).toLowerCase() === 'true'

    case 'json':
      // Try to parse JSON; return null if invalid
      try {

        return JSON.parse(value)
      } catch (error) {

        return null // Return null if JSON parsing fails
      }

    default:
      return value // Return original if type not matched
  }
}
export const castValueForStep = (field, value, typesAndFieldsForCasting) => {

  value = value === undefined ? null : value

  if (typesAndFieldsForCasting.shouldBeString?.includes(field)) {
    return value !== null ? String(value) : null // Use null for unset values, cast to string otherwise
  }

  if (typesAndFieldsForCasting.shouldBeListOfString?.includes(field)) {
    if (Array.isArray(value)) {
      return value.map((item) => (item !== null ? String(item) : null))
    }

    return [] // Default to empty array
  }

  if (typesAndFieldsForCasting.shouldBeMap?.includes(field)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [key, val !== null ? val : null])
      ) // Use null for unset map values
    }

    return {} // Default to empty map
  }

  if (typesAndFieldsForCasting.shouldBeMapOfStrings?.includes(field)) {
    if (typeof value === 'object' && value !== null) {
      const newValue = {}

      Object.keys(value).forEach((key) => {
        newValue[key] = value[key] !== null ? String(value[key]) : null // Use null for unset values
      })

      return newValue
    }

    return {} // Default to empty map
  }

  if (typesAndFieldsForCasting.shouldBeNumberOrString?.includes(field)) {
    // Try to parse as a number, fallback to string, or null if unset
    return value !== null && !isNaN(value) ? Number(value) : value !== null ? String(value) : null
  }

  if (typesAndFieldsForCasting.shouldBeArrayOfNumbers?.includes(field)) {
    if (Array.isArray(value)) {
      return value.map((item) => (item !== null ? Number(item) : null))
    }

    return [] // Default to empty array
  }

  if (typesAndFieldsForCasting.shouldBeNumber?.includes(field)) {
    return value !== null && value !== '' ? Number(value) : null
  }

  return value // Return value as is if no typecasting is defined
}

export const castAllStepsValues = async (steps) => {
  const castedSteps = JSON.parse(JSON.stringify(steps))

  // Recursive function to cast each value deeply in an object or array
  const castDeep = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(castDeep) // Apply casting recursively to arrays
    } else if (typeof obj === 'object' && obj !== null) {
      const newObj = {}

      Object.keys(obj).forEach((key) => {
        const value = obj[key]

        newObj[key] =
          typeof value === 'object' && value !== null ? castDeep(value) : castValue(value)
      })

      return newObj
    } else {
      return castValue(obj) // Cast simple values directly
    }
  }

  const replaceStepValues = (items) => {
    items.forEach((item) => {
      // Apply castDeep to each property that contains an object or value
      item.properties = item.type === 'EXECUTE_PROCESS' ? castDeep(item.properties) : item.properties

      // Recursively handle nested structures
      if (item.properties?.steps?.length > 0) {
        replaceStepValues(item.properties.steps)
      }
      if (item.properties?.try?.steps?.length > 0) {
        replaceStepValues(item.properties.try.steps)
      }
      if (item.properties?.finally?.steps?.length > 0) {
        replaceStepValues(item.properties.finally.steps)
      }
      if (item.properties?.catch?.length > 0) {
        item.properties.catch.forEach((item2) => {
          replaceStepValues(item2.steps)
        })
      }
      if (item.properties?.conditions?.length > 0) {
        item.properties.conditions.forEach((item2) => {
          replaceStepValues(item2.steps)
        })
      }
      if (item.properties?.batches?.length > 0) {
        item.properties.batches.forEach((item2) => {
          replaceStepValues(item2.steps)
        })
      }
      if (isJdbcTransaction(item) && item.properties?.parameters?.steps?.length > 0) {
        replaceStepValues(item.properties.parameters.steps)
      }
    })
  }

  await replaceStepValues(castedSteps) // Use await in case async operations are needed

  return castedSteps
}

export const getObjectIfProxy = (obj) => {
  if (isProxy(obj)) {
    return toRaw(obj)
  }

  return obj
}

export const getTableOptionsForFetch = (options = null) => {
  const obj = {
    page: 1,
    size: 25
  }

  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      if (key !== 'options' && key !== 'resetPage' && value) {
        obj[key] = value
      }
    })

    if (options.options?.itemsPerPage !== -1) {
      obj.page = options.resetPage ? 1 : options.options?.page || 1
      obj.size = options.options?.itemsPerPage || 25
    }

    if (options.options) {
      const sorting = getSortingFromTableOptions(options.options)

      if (sorting.orderField) obj.orderField = sorting.orderField
      if (sorting.orderDirection) obj.orderDirection = sorting.orderDirection
    }
  }

  return obj
}

export const getSortingFromTableOptions = (options, lowerCaseDirection = false) => {
  const sorting = {
    orderField: null,
    orderDirection: null
  }
  const optionsExtractedFromProxy = getObjectIfProxy(options)

  if (optionsExtractedFromProxy?.sortBy) {
    const sortBy = getObjectIfProxy(optionsExtractedFromProxy.sortBy);

    if (sortBy?.length && sortBy?.[0]?.key) sorting.orderField = sortBy[0].key
    if (sortBy?.length && sortBy?.[0]?.order)
      sorting.orderDirection = lowerCaseDirection ? sortBy[0].order : sortBy[0].order.toUpperCase()
  }
  return sorting
}

const mimeToExtension = {
  // Font MIME types
  'font/ttf': '.ttf',
  'font/otf': '.otf',
  'font/woff': '.woff',
  'font/woff2': '.woff2',
  'application/vnd.ms-fontobject': '.eot',
  'image/svg+xml': '.svg', // SVG used for images and fonts

  // Image MIME types
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/gif': '.gif',
  'image/webp': '.webp',
  'image/bmp': '.bmp',
  'image/tiff': '.tiff',
  'image/x-icon': '.ico',
  'image/vnd.microsoft.icon': '.ico',
  'image/avif': '.avif',

  // Document MIME types
  'application/pdf': '.pdf',
  'application/json': '.json',
  'application/java-archive': '.jar',

  'text/javascript': '.js',
  'application/javascript': '.js',
  'text/css': '.css',
  'text/html': '.html',
  'text/markdown': '.md'
};

export const getExtensionFromMimeType = (mimeType) => {
  return mimeToExtension[mimeType] || '';
}

export const getMimeTypeFromExtension = (extension) => {
  return Object.keys(mimeToExtension).find((key) => mimeToExtension[key] === extension);
}

export const getFullCleanFileNameFromContentType = (fileName, contentType) => {
  return fileName
    .replace(/ /g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    + getExtensionFromMimeType(contentType)
}


export const getValidationRuleByName = async (name) => {
  const res = await getValidationRule({
    name,
    $queryParameters: {
      size: 1
    }
  })

  const rule = res.data?.data?.items?.[0]

  if (!rule) throw new Error(`Validation rule with name "${name}" not found.`)

  return rule
}


const STATUS_STRING_TO_CODE = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  TIMEOUT: 408,
  CONFLICT: 409,
  VALIDATION: 422,
  LOCKED: 423,
  RATE_LIMITED: 429,
  NOT_IMPLEMENTED: 501,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
  INTERNAL_SERVER_ERROR: 500
}

export function normalizeHttpStatus(resLike) {
  if (!resLike) return 200
  if (typeof resLike.status === 'number') return resLike.status
  if (typeof resLike.statusCode === 'number') return resLike.statusCode
  if (typeof resLike.statusCode === 'string' && STATUS_STRING_TO_CODE[resLike.statusCode]) {
    return STATUS_STRING_TO_CODE[resLike.statusCode]
  }
  if (resLike.status === 'FAILED') return 500
  return 200
}

export function responseStatusText(resLike) {
  return (
    resLike?.statusText ||
    resLike?.data?.statusText ||
    resLike?.response?.statusText ||
    resLike?.response?.data?.statusText ||
    null
  )
}

export function i18nMessageForStatus(status, L, resLike) {
  const lang = L || {}
  switch (status) {
    case 400: return lang?.errors?.dataEmpty
    case 401: return lang?.errors?.unauthenticated
    case 403: return lang?.errors?.unauthorized
    case 404: return lang?.errors?.notFound
    case 408: return lang?.errors?.timeout
    case 409: return lang?.errors?.conflict
    case 422: return lang?.errors?.validation
    case 423: return lang?.errors?.locked
    case 429: return lang?.errors?.rateLimited
    case 501: return lang?.errors?.noShadowObject
    case 502: return lang?.errors?.badGateway
    case 503: return lang?.errors?.serviceUnavailable
    case 504: return lang?.errors?.gatewayTimeout
    case 500: {
      const isNullData = resLike?.data === null || resLike?.response?.data === null
      return isNullData ? lang?.errors?.dataEmpty : lang?.hints?.technicalDifficulty
    }
    default: return null
  }
}

export function getResponseBody(resLike) {
  return resLike?.data?.body ?? resLike?.body ?? null
}

export function isNoDataBody(body) {
  const isEmptyArray = Array.isArray(body) && body.length === 0
  const isEmptyMeta = body?.meta?.totalItems === 0
  return !body || isEmptyArray || isEmptyMeta
}


export function OmitByKey(value, keysToOmit = []) {
  if (Array.isArray(value)) {
    return value.map(v => OmitByKey(v, keysToOmit));
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (keysToOmit.includes(k)) continue;
      out[k] = OmitByKey(v, keysToOmit);
    }
    return out;
  }
  return value;
}


export async function fetchWithOrTags(apiFn, baseQuery, tagNames = []) {
  const tags = (tagNames || []).filter(Boolean);

  // 0 or 1 tag
  if (tags.length <= 1) {
    const payload = { ...baseQuery };
    if (tags.length === 1) payload.tags = tags[0];
    const res = await apiFn(payload);
    return res?.data?.data ?? { items: [], meta: { totalItems: 0 } };
  }

  // 2+ tags
  const page = baseQuery.page || 1;
  const size = baseQuery.size || 25;

  const responses = await Promise.all(
    tags.map(tag => apiFn({ ...baseQuery, tags: tag }))
  );

  // union by id
  const map = new Map();
  for (const r of responses) {
    for (const item of (r?.data?.data?.items || [])) {
      map.set(item.id, item);
    }
  }
  const merged = Array.from(map.values());

  // sort
  const field = baseQuery.orderField || 'name';
  const asc = String(baseQuery.orderDirection || 'ASC').toUpperCase() === 'ASC';
  merged.sort((a, b) =>
    a[field] > b[field] ? (asc ? 1 : -1)
      : a[field] < b[field] ? (asc ? -1 : 1)
        : 0
  );

  // paginate
  const start = (page - 1) * size;
  return {
    items: merged.slice(start, start + size),
    meta: { totalItems: merged.length }
  };
}

/**
 * Safely parse markdown to HTML with XSS protection.
 * Uses DOMPurify to sanitize the output from marked.
 * @param {string} markdown - The markdown string to parse
 * @returns {string} - Sanitized HTML string
 */
export const safeMarkdownParse = (markdown) => {
  if (typeof markdown !== 'string') return '';
  if (markdown === '') return '';
  try {
    return DOMPurify.sanitize(markedInstance.parse(markdown));
  } catch (e) {
    // Render the raw text escaped, so the user still sees something.
    return `<pre>${escapeHtml(markdown)}</pre>`;
  }
}

/**
 * Escape a string for safe interpolation into HTML text / attribute contexts.
 * Mirrors the standard 5-character HTML entity replacement set.
 * @param {*} value - Any value; will be coerced to string before escaping.
 * @returns {string} - HTML-escaped string.
 */
export const escapeHtml = (value) => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

/**
 * Build a self-contained HTML5 document that wraps sanitized markdown HTML
 * for preview in a new tab (blob URL).
 *
 * The body html is assumed to be already sanitized (e.g. via safeMarkdownParse).
 * The title is HTML-escaped here so resource names containing markup like
 * "</title><img src=x onerror=...>" cannot break out of the <title> RAWTEXT
 * context and execute script in the same-origin blob document.
 *
 * @param {Object} params
 * @param {string} params.title - Plain-text title; will be HTML-escaped.
 * @param {string} params.bodyHtml - Pre-sanitized HTML to place inside <body>.
 * @returns {string} - Full HTML document string.
 */
export const buildMarkdownPreviewDocument = ({ title, bodyHtml }) => {
  const safeTitle = escapeHtml(title)
  const safeBody = typeof bodyHtml === 'string' ? bodyHtml : ''
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob: https:; style-src 'unsafe-inline'; font-src data:; base-uri 'none'; form-action 'none';">
  <title>${safeTitle}</title>
</head>
<body>
${safeBody}
</body>
</html>`
}


export async function downloadFile(content, {
  fileName = 'download',
  mimeType = 'application/octet-stream',
  headers = {}
} = {}) {
  const blob = content instanceof Blob
    ? content
    : new Blob([content], { type: mimeType })

  // Try filename from Content-Disposition
  const disposition =
    headers['content-disposition'] ||
    headers['Content-Disposition']

  if (disposition) {
    const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\1?/)
    if (match?.[2]) {
      fileName = match[2]
    }
  }

  // Detect content-type
  let contentType =
    blob.type ||
    headers['content-type'] ||
    headers['Content-Type'] ||
    ''

  // If filename has NO extension → decide extension
  if (!fileName.includes('.')) {

    // Read first bytes (magic numbers)
    const buffer = await blob.slice(0, 4).arrayBuffer()
    const hex = Array
      .from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')

    const magicMap = {
      'ffd8ffe0': 'jpg',
      'ffd8ffe1': 'jpg',
      'ffd8ffe2': 'jpg',
      '89504e47': 'png',
      '47494638': 'gif',
      '25504446': 'pdf',
      '52494646': 'webp'
    }

    let ext = magicMap[hex]

    // Fallback to content-type only
    if (!ext) {
      const dotExt = mimeToExtension[contentType]
      ext = dotExt ? dotExt.slice(1) : undefined
    }

    if (ext) {
      fileName = `${fileName}.${ext}`
    }
  }

  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')

  link.href = url
  link.download = fileName
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function copyTextToClipboard(text) {
  if (!text) return false
  try {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
      return true
    }
    if (window?.clipboardData?.setData) {
      window.clipboardData.setData('Text', text)
      return true
    }
    return false
  } catch (err) {
    console.error('copyTextToClipboard failed', err)
    return false
  }
}
